import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import axios, { type AxiosRequestHeaders } from 'axios'

import {
  clientTreatmentCategoryFilterService,
  TreatmentCategoryFilterService,
} from './treatment-category-filter.service'

import { ApiHeaders } from '@/shared/libs/context-enhancer'
import { createSolarApiClient } from '@/shared/libs/api-client'

import type { QueryClient } from '@tanstack/react-query'
import type {
  TreatmentFilterResponse,
  TreatmentCategory,
  TreatmentGroup,
  Treatment,
} from '@/features/treatment-category-filter/types/treatment-filter.type'

/* API */
export const getTreatmentFilter = async (headers: ApiHeaders): Promise<TreatmentFilterResponse> => {
  const treatmentFilterService = new TreatmentCategoryFilterService(
    createSolarApiClient({ headers: headers as unknown as AxiosRequestHeaders }),
  )
  return treatmentFilterService.getTreatmentCategoryFilter()
}

/* CLIENT SIDE */
export const useTreatmentFilterReader = (headers: ApiHeaders) => {
  const queryClient = useQueryClient()
  const locale = headers['Accept-Language']
  const initialData = queryClient.getQueryData(getTreatmentFilterQueryKey(locale))

  return useQuery({
    ...getTreatmentFilterQueryOptions(headers),
    initialData: initialData as TreatmentFilterResponse | undefined,
  })
}

/* SERVER SIDE */
export const prefetchTreatmentFilter = async (
  queryClient: QueryClient,
  headers: ApiHeaders,
): Promise<TreatmentFilterResponse | undefined> => {
  const locale = headers['Accept-Language']
  await queryClient.prefetchQuery(getTreatmentFilterQueryOptions(headers))
  return queryClient.getQueryData(getTreatmentFilterQueryKey(locale))
}

/* SHARED */
export const getTreatmentFilterQueryKey = (locale: string) => {
  return ['treatment-filter', locale]
}

export const getTreatmentFilterQueryOptions = (headers: ApiHeaders) => {
  const locale = headers['Accept-Language']
  return queryOptions({
    queryKey: getTreatmentFilterQueryKey(locale),
    queryFn: () => {
      return getTreatmentFilter(headers)
    },
    staleTime: 60 * 60 * 1000,
  })
}

/* UTIL */

interface TreatmentCategoryUnit {
  id: number
  name: string
  count: number
}
export class TreatmentFilterResponseController {
  private data: TreatmentFilterResponse | undefined

  constructor(data: TreatmentFilterResponse | undefined) {
    this.data = data
  }

  public getTreatmentCategoryList(): TreatmentCategory[] {
    return this.data?.treatmentCategories || []
  }

  public getAllUnitList(): TreatmentCategoryUnit[] {
    return this.getTreatmentCategoryList().flatMap((category) => [
      ...category.treatmentGroups.map((group) => ({
        id: group.id,
        name: group.name,
        count: group.count,
      })),
      ...category.treatmentGroups.flatMap((group) =>
        group.treatments.map((treatment) => ({
          id: treatment.id,
          name: treatment.name,
          count: treatment.count,
        })),
      ),
    ])
  }

  public getTreatmentGroupById(id: number): TreatmentGroup | undefined {
    return this.data?.treatmentCategories
      .flatMap((category) => category.treatmentGroups)
      .find((group) => group.id === id)
  }

  public getCategoryUnitByName(name: string): TreatmentCategoryUnit | undefined {
    for (const category of this.getTreatmentCategoryList()) {
      if (category.name === name) {
        return {
          id: category.id,
          name: category.name,
          count: category.count,
        }
      }
      for (const group of category.treatmentGroups) {
        if (group.name === name) {
          return {
            id: group.id,
            name: group.name,
            count: group.count,
          }
        }
        for (const treatment of group.treatments) {
          if (treatment.name === name) {
            return {
              id: treatment.id,
              name: treatment.name,
              count: treatment.count,
            }
          }
        }
      }
    }
  }

  public getTreatmentCategoryByName(name: string): TreatmentCategory | undefined {
    return this.data?.treatmentCategories.find((category) => category.name === name)
  }

  public getCategoryCombinationByNameAndId(
    name: string,
    id?: number,
  ): {
    treatmentCategories?: TreatmentCategory
    treatmentGroups?: TreatmentGroup
    treatments?: Treatment
  } {
    const noId: boolean = id === undefined
    for (const treatmentCategory of this.getTreatmentCategoryList()) {
      if (noId ? treatmentCategory.name === name : treatmentCategory.name === name && treatmentCategory.id === id) {
        return {
          treatmentCategories: treatmentCategory,
          treatmentGroups: undefined,
          treatments: undefined,
        }
      }
      for (const treatmentGroup of treatmentCategory.treatmentGroups) {
        if (noId ? treatmentGroup.name === name : treatmentGroup.name === name && treatmentGroup.id === id) {
          return {
            treatmentCategories: treatmentCategory,
            treatmentGroups: treatmentGroup,
            treatments: undefined,
          }
        }
        for (const treatment of treatmentGroup.treatments) {
          if (noId ? treatment.name === name : treatment.name === name && treatment.id === id) {
            return {
              treatmentCategories: treatmentCategory,
              treatmentGroups: treatmentGroup,
              treatments: treatment,
            }
          }
        }
      }
    }

    return {
      treatmentCategories: undefined,
      treatmentGroups: undefined,
      treatments: undefined,
    }
  }
}
